import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, CheckCheck, Columns3, Download, FileQuestion, Inbox as InboxIcon, Rows3, Search, X } from 'lucide-react';
import useKeyboardShortcuts from '@/hooks/useKeyboardShortcuts';
import useBriefStatus, { STATUS_LABELS } from '@/hooks/useBriefStatus';
import useAssignments from '@/hooks/useAssignments';
import useMediaQuery from '@/hooks/useMediaQuery';
import { useAuth } from '@/context/AuthContext';
import { useTimeRange } from '@/context/TimeRangeContext';
import { useCommandPalette } from '@/components/palette/CommandPalette';
import { EmptyState, ErrorState, LoadingState, PageHeader, useConfirm, describeError } from '@/components/primitives';
import { normalizeSeverity, SEVERITY } from '@/lib/severity';
import { HANDLED, slaFor, useSlaPolicy } from '@/lib/sla';
import useInboxBriefs from '@/pages/inbox/useInboxBriefs';
import useBriefRules from '@/pages/inbox/useBriefRules';
import ShortcutHelp from '@/pages/inbox/ShortcutHelp';
import { buildBriefActions } from '@/pages/inbox/actionDescriptors';
import { briefId, copyText, downloadBlob, groupIocs, matchesQuery, shortId } from '@/pages/inbox/inboxUtils';
import ThreatTable, { COLUMNS, DEFAULT_COLUMNS } from '@/pages/threats/ThreatTable';
import DetailPanel from '@/pages/threats/DetailPanel';
import '@/styles/inbox.css';
import '@/styles/threats.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 50;
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];
const TYPES = [
  { key: 'main', label: 'Attacks and advisories' },
  { key: 'informational', label: 'Informational' },
  { key: 'all', label: 'All types' },
];
const typeOk = (b, t) => (t === 'all' ? true : t === 'informational' ? b.content_type === 'informational' : b.content_type !== 'informational');
const SEV_RANK = (b) => SEVERITY[normalizeSeverity(b.severity)]?.rank ?? 0;
const STATUS_RANK = { new: 0, triage: 1, acked: 2, shipped: 3, archived: 4 };

const store = {
  get(k, d) { try { const v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch { return d; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* per-browser preference only */ } },
};

// A Radix dialog, alert dialog or menu is open somewhere: page shortcuts stand down.
const layerOpen = () => Boolean(document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"]'));

/**
 * The threat queue: saved views (open, overdue, unassigned, mine, handled),
 * severity and type filters, a sortable table with SLA due times and owners,
 * and the selected threat in a panel. Keyboard first: J/K to move, A to
 * acknowledge, I to take ownership, X to select, / to filter, ? for the rest.
 */
export default function Threats() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const me = user ? { id: user.id, email: user.email } : null;
  const [params, setParams] = useSearchParams();
  const urlId = params.get('id');
  const { days, range } = useTimeRange();
  const { isOpen: paletteOpen } = useCommandPalette();
  const slaHours = useSlaPolicy();
  const { getStatus, getItem, setStatus, markMany, loaded: statusLoaded, error: statusError, reload: reloadStatus } = useBriefStatus();
  const { getOwner, assign, assignMany } = useAssignments();
  const { briefs, loading, error, refreshError, lastUpdated, hasMore, loadingMore, reload, refresh, loadOlder, loadAll } = useInboxBriefs();
  const [confirm, confirmDialog] = useConfirm();
  const isWide = useMediaQuery('(min-width: 1280px)');
  const isPhone = useMediaQuery('(max-width: 767.98px)');

  // ?view= and ?day= let Home and charts link straight to a slice of the queue.
  const [queue, setQueue] = useState(() => params.get('view') || store.get('is.threats.queue', 'open'));
  const [day, setDay] = useState(() => (/^\d{4}-\d{2}-\d{2}$/.test(params.get('day') || '') ? params.get('day') : null));
  const [type, setType] = useState('main');
  const [severities, setSeverities] = useState([]);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'due', dir: 1 });
  const [page, setPage] = useState(0);
  const [columns, setColumns] = useState(() => store.get('is.threats.columns', DEFAULT_COLUMNS));
  const [density, setDensity] = useState(() => store.get('is.threats.density', 'standard'));
  const [allDates, setAllDates] = useState(false); // a deep link to an older threat lifts the time range
  const [selectedId, setSelectedId] = useState(null);
  const [panelOpen, setPanelOpenState] = useState(false);
  // Read synchronously by effects that run in the same render as the change.
  const panelOpenRef = useRef(false);
  const setPanelOpen = useCallback((v) => { panelOpenRef.current = v; setPanelOpenState(v); }, []);
  const [notFoundId, setNotFoundId] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [helpOpen, setHelpOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const searchRef = useRef(null);
  const headingRef = useRef(null);
  const handledUrlRef = useRef(null);
  const triedAllRef = useRef(false);

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60_000); return () => clearInterval(t); }, []);
  useEffect(() => { store.set('is.threats.queue', queue); }, [queue]);
  useEffect(() => { store.set('is.threats.columns', columns); }, [columns]);
  useEffect(() => { store.set('is.threats.density', density); }, [density]);

  const getSla = useCallback((b, s, item) => slaFor(b, s, item, slaHours, now), [slaHours, now]);

  /* ---- saved views ---- */
  const QUEUES = useMemo(() => [
    { key: 'open', label: 'All open', test: (b, s) => !HANDLED.has(s) },
    { key: 'overdue', label: 'Overdue', test: (b, s) => !HANDLED.has(s) && getSla(b, s, getItem(briefId(b)))?.state === 'overdue', hot: true },
    { key: 'unassigned', label: 'Unassigned', test: (b, s) => !HANDLED.has(s) && !getOwner(briefId(b)) },
    { key: 'mine', label: 'Assigned to me', test: (b, s) => !HANDLED.has(s) && Boolean(getOwner(briefId(b))) },
    { key: 'handled', label: 'Handled', test: (b, s) => HANDLED.has(s) },
    { key: 'all', label: 'Everything', test: () => true },
  ], [getSla, getItem, getOwner]);

  const cutoff = now - days * 864e5;
  const inRange = useCallback((b) => (day ? String(b.discovered_at || '').slice(0, 10) === day : allDates || Date.parse(b.discovered_at) >= cutoff), [allDates, cutoff, day]);

  /* ---- filter, count and sort in one pass ---- */
  const { rows, queueCounts, sevCounts } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const qc = Object.fromEntries(QUEUES.map((v) => [v.key, 0]));
    const sc = Object.fromEntries(SEVERITIES.map((s) => [s, 0]));
    const current = QUEUES.find((v) => v.key === queue) || QUEUES[0];
    const out = [];
    briefs.forEach((b) => {
      if (!inRange(b) || !typeOk(b, type) || !matchesQuery(b, q)) return;
      const s = getStatus(briefId(b));
      const sev = normalizeSeverity(b.severity);
      const sevOk = !severities.length || severities.includes(sev);
      if (sevOk) QUEUES.forEach((v) => { if (v.test(b, s)) qc[v.key] += 1; });
      if (!current.test(b, s)) return;
      if (sc[sev] != null) sc[sev] += 1;
      if (sevOk) out.push(b);
    });
    const value = {
      severity: (b) => SEV_RANK(b),
      name: (b) => String(b.name || '').toLowerCase(),
      actor: (b) => String(b.threat_actor || '~').toLowerCase(),
      sources: (b) => b.source_count || 1,
      owner: (b) => (getOwner(briefId(b)) ? 0 : 1),
      status: (b) => STATUS_RANK[getStatus(briefId(b))] ?? 0,
      detected: (b) => Date.parse(b.discovered_at) || 0,
      due: (b) => { const s = getSla(b, getStatus(briefId(b)), getItem(briefId(b))); return s && !HANDLED.has(getStatus(briefId(b))) ? s.due : Number.MAX_SAFE_INTEGER; },
    }[sort.key];
    out.sort((a, b) => {
      const A = value(a); const B = value(b);
      return (A > B ? 1 : A < B ? -1 : 0) * sort.dir || SEV_RANK(b) - SEV_RANK(a) || (Date.parse(b.discovered_at) - Date.parse(a.discovered_at));
    });
    return { rows: out, queueCounts: qc, sevCounts: sc };
  }, [briefs, QUEUES, queue, type, severities, query, inRange, sort, getStatus, getOwner, getSla, getItem]);

  // The view and day travel in the URL only on arrival; afterwards the page owns them.
  useEffect(() => {
    if (!params.get('view') && !params.get('day')) return;
    const next = new URLSearchParams(params); next.delete('view'); next.delete('day');
    setParams(next, { replace: true });
  }, []); // run once on arrival

  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const pageRows = rows.slice(page * PAGE, page * PAGE + PAGE);
  const pendingPageRef = useRef(null);
  useEffect(() => {
    // A deep link asks for the page that holds it; any other filter change starts at the top.
    setPage(pendingPageRef.current ?? 0);
    pendingPageRef.current = null;
  }, [queue, type, severities, query, days, sort, day]);
  useEffect(() => {
    if (pendingPageRef.current != null) { setPage(pendingPageRef.current); pendingPageRef.current = null; }
  }, [selectedId]);
  useEffect(() => { if (page > pages - 1) setPage(pages - 1); }, [page, pages]);

  const selected = selectedId ? briefs.find((b) => briefId(b) === selectedId) || null : null;
  const selectedStatus = selected ? getStatus(selectedId) : 'new';
  const rules = useBriefRules(selected && panelOpen ? selectedId : null);

  /* ---- deep links: /threats?id=<attack_id> ---- */
  useEffect(() => {
    if (!urlId || loading || error) return;
    if (!statusLoaded && !statusError) return;
    if (handledUrlRef.current === urlId) return;
    const match = briefs.find((b) => briefId(b) === urlId || String(b.id) === urlId);
    if (!match) {
      if (hasMore && !triedAllRef.current) { triedAllRef.current = true; loadAll().catch(() => {}); return; }
      if (loadingMore) return;
      handledUrlRef.current = urlId;
      setNotFoundId(urlId);
      setPanelOpen(true);
      return;
    }
    const id = briefId(match);
    handledUrlRef.current = urlId;
    const index = rows.findIndex((b) => briefId(b) === id);
    if (index < 0) {
      setQueue('all'); setType('all'); setSeverities([]); setQuery('');
      if (!inRange(match)) setAllDates(true);
      toast.info('Filters were cleared to show the linked threat.');
    } else {
      pendingPageRef.current = Math.floor(index / PAGE);
    }
    setSelectedId(id);
    setPanelOpen(true);
  }, [urlId, briefs, loading, error, statusLoaded, statusError, hasMore, loadingMore, loadAll, rows, inRange, setPanelOpen]);

  /* ---- keep the cursor on a visible row (never over a selection made in the same render) ---- */
  useEffect(() => {
    if (loading || !pageRows.length) return;
    if (urlId && handledUrlRef.current !== urlId) return;
    setSelectedId((cur) => (cur && (panelOpenRef.current || pageRows.some((b) => briefId(b) === cur)) ? cur : briefId(pageRows[0])));
  }, [loading, pageRows, urlId]);

  /* ---- mirror the open threat into the URL (replace, never push) ---- */
  useEffect(() => {
    if (!panelOpen || !selectedId || notFoundId) return;
    if (urlId === selectedId) return;
    handledUrlRef.current = selectedId;
    const next = new URLSearchParams(params); next.set('id', selectedId);
    setParams(next, { replace: true });
  }, [panelOpen, selectedId, notFoundId, urlId, params, setParams]);

  const open = useCallback((id, { focus = false } = {}) => {
    setNotFoundId(null);
    setSelectedId(id);
    setPanelOpen(true);
    if (focus || isPhone) requestAnimationFrame(() => headingRef.current?.focus({ preventScroll: true }));
  }, [isPhone]);
  const closePanel = useCallback(() => {
    setPanelOpen(false);
    setNotFoundId(null);
    const next = new URLSearchParams(params); next.delete('id');
    handledUrlRef.current = null;
    setParams(next, { replace: true });
    requestAnimationFrame(() => document.querySelector(`#threat-row-${CSS.escape(selectedId || '')} .th-title`)?.focus());
  }, [params, setParams, selectedId]);

  const moveCursor = (delta) => {
    if (!rows.length) return;
    const i = Math.max(0, rows.findIndex((b) => briefId(b) === selectedId));
    const ni = Math.min(rows.length - 1, Math.max(0, i + delta));
    const id = briefId(rows[ni]);
    if (Math.floor(ni / PAGE) !== page) setPage(Math.floor(ni / PAGE));
    setSelectedId(id);
    requestAnimationFrame(() => document.getElementById(`threat-row-${id}`)?.scrollIntoView({ block: 'nearest' }));
  };
  const advanceFrom = useCallback((id) => {
    const i = rows.findIndex((b) => briefId(b) === id);
    const next = rows[i + 1] || rows[i - 1];
    if (next) setSelectedId(briefId(next));
  }, [rows]);

  /* ---- actions ---- */
  const changeStatus = useCallback(async (brief, next, { advance = false } = {}) => {
    const id = briefId(brief);
    const prev = getStatus(id);
    if (prev === next) return;
    if (advance && queue !== 'all' && queue !== 'handled') advanceFrom(id);
    try { await setStatus(id, next); } catch (err) { toast.error("Couldn't update the status", { description: describeError(err) }); return; }
    toast.success(next === 'new' ? 'Moved back to New' : STATUS_LABELS[next], {
      description: brief.name,
      action: { label: 'Undo', onClick: () => setStatus(id, prev).catch((err) => toast.error("Couldn't undo", { description: describeError(err) })) },
    });
  }, [getStatus, setStatus, advanceFrom, queue]);

  const changeOwner = useCallback(async (brief, owner) => {
    const id = briefId(brief);
    const had = getOwner(id);
    try { await assign(id, owner); } catch (err) { toast.error("Couldn't change the owner", { description: describeError(err) }); return; }
    toast.success(owner ? 'Assigned to you' : 'Unassigned', {
      description: brief.name,
      action: { label: 'Undo', onClick: () => assign(id, had ? 'me' : null).catch((err) => toast.error("Couldn't undo", { description: describeError(err) })) },
    });
  }, [assign, getOwner]);

  const exportRules = useCallback(async (brief, kind, count) => {
    const id = briefId(brief);
    const label = kind === 'yara' ? 'YARA' : 'Sigma';
    try {
      const res = await axios.post(`${API}/dashboard/export-rules/${encodeURIComponent(id)}`, {}, { params: { rule_type: kind }, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, responseType: 'blob' });
      downloadBlob(res.data, `${kind}_rules_${id}.${kind === 'yara' ? 'yar' : 'yml'}`);
    } catch (err) { toast.error(`Couldn't download ${label} rules`, { description: describeError(err) }); return; }
    const prev = getStatus(id);
    const mark = prev !== 'shipped' && prev !== 'archived';
    toast.success(`${label} rules downloaded (${count})`, { description: mark ? 'Status set to Rules exported.' : undefined });
    if (mark) setStatus(id, 'shipped').catch(() => toast.error("The file downloaded, but the status couldn't be updated"));
  }, [getStatus, setStatus]);

  const copyIocs = useCallback(async (brief) => {
    const values = groupIocs(brief.iocs).flatMap((g) => g.items.map((i) => i.value));
    try { await copyText(values.join('\n')); toast.success(`Copied ${values.length} IOC${values.length === 1 ? '' : 's'}`); } catch { toast.error("Couldn't copy to the clipboard"); }
  }, []);
  const downloadIocs = useCallback((brief) => {
    const header = ['# IntelliSecure IOC export', `# Threat: ${brief.name || ''}`, `# ID: ${brief.attack_id || ''}`, `# Severity: ${brief.severity || ''}`,
      brief.source_url ? `# Source: ${brief.source_url}` : null, `# Exported: ${new Date().toISOString()}`, `# Count: ${brief.iocs.length}`, ''].filter((l) => l != null).join('\n');
    downloadBlob(`${header}\n${brief.iocs.join('\n')}\n`, `iocs_${brief.attack_id || 'threat'}.txt`);
    toast.success(`${brief.iocs.length} IOCs downloaded`);
  }, []);

  const actions = useMemo(() => buildBriefActions({ brief: selected, status: selectedStatus, rules, handlers: { changeStatus, exportRules, copyIocs, downloadIocs } }),
    [selected, selectedStatus, rules, changeStatus, exportRules, copyIocs, downloadIocs]);

  /* ---- bulk ---- */
  const checkedIds = [...checked].filter((id) => briefs.some((b) => briefId(b) === id));
  const bulk = async (kind) => {
    const ids = checkedIds;
    if (!ids.length) return;
    if (kind === 'assign') {
      try { const n = await assignMany(ids, 'me'); toast.success(`${n} threat${n === 1 ? '' : 's'} assigned to you`); } catch (err) { toast.error("Couldn't assign the threats", { description: describeError(err) }); return; }
    } else {
      if (kind === 'archived' && ids.length > 1) {
        const ok = await confirm({ title: `Archive ${ids.length} threats?`, description: 'They leave the open views. You can undo this from the confirmation message.', confirmLabel: `Archive ${ids.length}` });
        if (!ok) return;
      }
      const previous = {}; ids.forEach((id) => { previous[id] = getStatus(id); });
      try { await markMany(ids, kind); } catch (err) { toast.error("Couldn't update the threats", { description: describeError(err) }); return; }
      toast.success(`${ids.length} threat${ids.length === 1 ? '' : 's'} ${STATUS_LABELS[kind].toLowerCase()}`, {
        action: { label: 'Undo', onClick: async () => {
          const by = {}; Object.entries(previous).forEach(([id, s]) => { (by[s] = by[s] || []).push(id); });
          try { await Promise.all(Object.entries(by).map(([s, list]) => markMany(list, s))); } catch (err) { toast.error("Couldn't undo", { description: describeError(err) }); }
        } },
      });
    }
    setChecked(new Set());
  };
  const toggle = (id) => setChecked((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setChecked((c) => { const all = pageRows.every((b) => c.has(briefId(b))); const n = new Set(c); pageRows.forEach((b) => (all ? n.delete(briefId(b)) : n.add(briefId(b)))); return n; });

  const exportView = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [['ID', 'Severity', 'Threat', 'Type', 'Actor', 'CVEs', 'Sources', 'Owner', 'Status', 'Due (UTC)', 'Detected (UTC)'].join(',')];
    rows.forEach((b) => {
      const id = briefId(b); const s = getStatus(id); const sla = getSla(b, s, getItem(id));
      lines.push([id, normalizeSeverity(b.severity), b.name, b.content_type || 'attack', b.threat_actor || '', (b.cves || []).join(' '), b.source_count || 1,
        getOwner(id) ? 'You' : '', STATUS_LABELS[s], sla ? new Date(sla.due).toISOString() : '', b.discovered_at].map(esc).join(','));
    });
    downloadBlob(`${lines.join('\n')}\n`, `intellisecure_threats_${queue}_${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv;charset=utf-8');
    toast.success(`Exported ${rows.length} threats`, { description: 'The current view, filters and sort.' });
  };

  /* ---- keyboard ---- */
  const guard = (fn) => (e) => { if (!layerOpen()) fn(e); };
  const run = (id) => {
    if (layerOpen() || !selected) return;
    const a = actions.byId[id];
    if (!a) return;
    if (a.disabled) { if (a.reason) toast.info(a.reason); return; }
    a.run();
  };
  useKeyboardShortcuts({
    j: guard(() => moveCursor(1)),
    k: guard(() => moveCursor(-1)),
    'g g': guard(() => moveCursor(-rows.length)),
    'shift+g': guard(() => moveCursor(rows.length)),
    enter: guard(() => { if (selectedId) open(selectedId, { focus: true }); }),
    x: guard(() => { if (selectedId) toggle(selectedId); }),
    i: guard(() => { if (selected) changeOwner(selected, getOwner(selectedId) ? null : 'me'); }),
    t: () => run('triage'),
    a: (e) => run(e.shiftKey ? 'archive' : 'ack'),
    'shift+a': () => run('archive'),
    e: (e) => run(e.shiftKey ? 'yara' : 'sigma'),
    'shift+e': () => run('yara'),
    c: (e) => run(e.shiftKey ? 'downloadIocs' : 'copyIocs'),
    'shift+c': () => run('downloadIocs'),
    '/': guard(() => searchRef.current?.focus()),
    '?': guard(() => setHelpOpen(true)),
    'shift+?': guard(() => setHelpOpen(true)),
    escape: guard(() => { if (panelOpen) closePanel(); else if (checked.size) setChecked(new Set()); }),
  }, { enabled: !helpOpen && !paletteOpen });

  const filtersActive = severities.length > 0 || query.trim() || type !== 'main' || allDates || day;
  const clearFilters = () => { setSeverities([]); setQuery(''); setType('main'); setAllDates(false); setDay(null); };
  const panelMode = isPhone ? 'screen' : isWide ? 'side' : 'drawer';
  const showPanel = panelOpen && (selected || notFoundId);

  let body;
  if (loading && !briefs.length) body = <section className="th-surface"><LoadingState label="threats" rows={8} /></section>;
  else if (error && !briefs.length) body = <section className="th-surface"><ErrorState title="Couldn't load the threat queue" error={error} onRetry={reload} /></section>;
  else if (!briefs.length) {
    body = <section className="th-surface"><EmptyState icon={InboxIcon} title="No threats yet" description="Threats appear here when monitored sources report activity that matches your organization profile." action={<button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => navigate('/settings')}>Review your profile</button>} /></section>;
  } else {
    body = (
      <section className={`th-surface th-density--${density}`} aria-label="Threat queue">
        {rows.length ? (
          <>
            <ThreatTable
              rows={pageRows} columns={columns} sort={sort}
              onSort={(key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : (['name', 'actor', 'due', 'owner', 'status'].includes(key) ? 1 : -1) }))}
              selectedId={selectedId} onOpen={open} checked={checked} onToggle={toggle} onToggleAll={toggleAll}
              getStatus={getStatus} getStatusItem={getItem} getOwner={getOwner} getSla={getSla} me={me}
              onQuickAck={(b) => changeStatus(b, 'acked')} onQuickAssign={(b) => changeOwner(b, 'me')}
              panelOpen={showPanel && panelMode === 'side'}
            />
            <div className="th-foot">
              <span>{page * PAGE + 1}–{page * PAGE + pageRows.length} of {rows.length}{hasMore ? '+' : ''}</span>
              <span className="th-foot-actions">
                {hasMore && <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={() => loadOlder().catch((err) => toast.error("Couldn't load older threats", { description: describeError(err) }))} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'Load older threats'}</button>}
                <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => setPage((p) => p - 1)} disabled={page === 0}>Previous</button>
                <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => setPage((p) => p + 1)} disabled={page >= pages - 1}>Next</button>
              </span>
            </div>
          </>
        ) : (
          <EmptyState
            icon={queue === 'overdue' ? CheckCheck : InboxIcon}
            title={queue === 'overdue' && !filtersActive ? 'Nothing is overdue' : queue === 'mine' && !filtersActive ? 'Nothing is assigned to you' : 'No threats in this view'}
            description={queue === 'overdue' && !filtersActive ? 'Every open threat is inside its response target.' : queue === 'mine' && !filtersActive ? 'Press I on a threat, or use Assign to me, to take ownership.' : `Widen the time range (${range.label.toLowerCase()}), clear a filter or pick another view.`}
            action={filtersActive ? <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={clearFilters}>Clear filters</button> : null}
          />
        )}
      </section>
    );
  }

  const panel = showPanel && (notFoundId ? (
    <aside className={`th-panel th-panel--${panelMode}`} aria-label="Threat not found">
      <EmptyState icon={FileQuestion} title="Threat not found" description={`The linked threat (ID ${shortId(notFoundId)}) is not in your queue. It may have been removed, or the link came from another organization.`}
        action={<button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={closePanel}>Back to the queue</button>} />
    </aside>
  ) : (
    <DetailPanel
      ref={headingRef} brief={selected} status={selectedStatus} statusItem={getItem(selectedId)} owner={getOwner(selectedId)} me={me}
      sla={getSla(selected, selectedStatus, getItem(selectedId))} rules={rules} actions={actions}
      onClose={closePanel} onAssign={changeOwner} mode={panelMode}
    />
  ));

  return (
    <div className={`th-page ${showPanel ? `has-panel has-panel--${panelMode}` : ''}`} data-testid="threats-page">
      <div className="th-main">
        <PageHeader
          title="Threats"
          subtitle={`${rows.length} in this view, ${day ? `detected on ${day}` : allDates ? 'all dates' : range.label.toLowerCase()}. ${sort.key === 'due' ? 'Most urgent first.' : ''}`}
          actions={(
            <>
              {(refreshError || statusError) && (
                <button type="button" className="is-btn is-btn--ghost is-btn--sm th-warn" onClick={() => { refresh(); reloadStatus(); }}>
                  Couldn't refresh{lastUpdated ? `, showing ${lastUpdated.toISOString().slice(11, 16)} UTC` : ''}. Retry
                </button>
              )}
              <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={exportView} disabled={!rows.length}><Download aria-hidden="true" /> Export view</button>
            </>
          )}
        />

        <div className="th-views" role="tablist" aria-label="Saved views">
          {QUEUES.map((v) => (
            <button key={v.key} type="button" role="tab" aria-selected={queue === v.key} onClick={() => setQueue(v.key)} data-testid={`view-${v.key}`}>
              {v.label} <span className={`th-n ${v.hot && queueCounts[v.key] ? 'is-hot' : ''}`}>{queueCounts[v.key]}</span>
            </button>
          ))}
        </div>

        <div className="th-toolbar">
          <label className="th-search">
            <Search aria-hidden="true" />
            <span className="sr-only">Filter this view</span>
            <input ref={searchRef} type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by title, actor, CVE or indicator" onKeyDown={(e) => { if (e.key === 'Escape') { setQuery(''); e.currentTarget.blur(); } }} />
            <kbd className="th-kbd">/</kbd>
          </label>
          <div className="th-chips" role="group" aria-label="Severity">
            {SEVERITIES.map((s) => (
              <button key={s} type="button" className="th-chip" data-sev={s} aria-pressed={severities.includes(s)} onClick={() => setSeverities((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]))}>
                {s} <span className="th-n">{sevCounts[s]}</span>
              </button>
            ))}
          </div>
          <div className="th-seg" role="group" aria-label="Type">
            {TYPES.map((t) => <button key={t.key} type="button" aria-pressed={type === t.key} onClick={() => setType(t.key)}>{t.label}</button>)}
          </div>
          {allDates && <button type="button" className="th-chip th-chip--on" onClick={() => setAllDates(false)}>All dates <X aria-hidden="true" /></button>}
          {day && <button type="button" className="th-chip th-chip--on" onClick={() => setDay(null)} aria-label={`Showing ${day} only. Clear the day filter`}>Day {day} <X aria-hidden="true" /></button>}
          {filtersActive && <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={clearFilters}>Clear filters</button>}
          <span className="th-spacer" />
          <Dropdown.Root>
            <Dropdown.Trigger asChild><button type="button" className="is-btn is-btn--ghost is-btn--sm th-hide-sm"><Columns3 aria-hidden="true" /> Columns</button></Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content align="end" sideOffset={6} className="sh-pop">
                <Dropdown.Label className="sh-pop-label">Show columns</Dropdown.Label>
                {COLUMNS.map((c) => (
                  <Dropdown.CheckboxItem key={c.key} className="sh-pop-item" checked={columns.includes(c.key)} onCheckedChange={(on) => setColumns((cur) => (on ? [...cur, c.key] : cur.filter((k) => k !== c.key)))} onSelect={(e) => e.preventDefault()}>
                    {c.label}
                    <Dropdown.ItemIndicator className="sh-pop-check"><Check className="sh-ic" aria-hidden="true" /></Dropdown.ItemIndicator>
                  </Dropdown.CheckboxItem>
                ))}
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
          <button type="button" className="sh-icon-btn th-hide-sm" onClick={() => setDensity((d) => (d === 'compact' ? 'standard' : 'compact'))} aria-label={`Row height: ${density}. Switch to ${density === 'compact' ? 'standard' : 'compact'}`} title={density === 'compact' ? 'Standard rows' : 'Compact rows'} aria-pressed={density === 'compact'}>
            <Rows3 className="sh-ic" aria-hidden="true" />
          </button>
        </div>

        {checkedIds.length > 0 && (
          <div className="th-bulk" role="region" aria-label="Bulk actions">
            <strong>{checkedIds.length} selected</strong>
            <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => bulk('assign')}>Assign to me</button>
            <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => bulk('acked')}>Acknowledge</button>
            <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => bulk('triage')}>Start triage</button>
            <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => bulk('archived')}>Archive</button>
            <button type="button" className="is-btn is-btn--ghost is-btn--sm th-spacer-left" onClick={() => setChecked(new Set())}>Clear selection</button>
          </div>
        )}

        {body}
      </div>
      {panel}
      {showPanel && panelMode === 'drawer' && <div className="th-scrim" onClick={closePanel} aria-hidden="true" />}
      <ShortcutHelp open={helpOpen} onOpenChange={setHelpOpen} />
      {confirmDialog}
    </div>
  );
}
